/** Setup / "Your Engine" tab — web mirror of `Sources/Views/SetupView.swift`.
 *
 * Pair a local engine and see its status without dropping to manual `curl`.
 * Three parts:
 *   1. Engine status card — from the polled `factory_health` row (daemon_alive +
 *      last_heartbeat + watchdog_state), with a clear Connected/Offline verdict
 *      and an empty state when no engine has ever reported.
 *   2. Pair a new engine — mint a `brdg_` token (POST /bridge-tokens), show the
 *      plaintext ONCE with a copy affordance + "you won't see this again", then
 *      the concise pairing steps (set BRIDGE_TOKEN, enable cloud_mirror, restart).
 *   3. Existing tokens — GET /bridge-tokens rows (metadata only) with a Revoke
 *      action (confirmed first). Never shows hashes/plaintext.
 *
 * Reads health from the polled snapshot and mint/list/revoke through the
 * `env.bridgeTokens` seam (Mock offline; BrainBridgeTokensStore live). */

import { useCallback, useEffect, useState } from 'react';
import { useAppState, useEnv } from '../app/context';
import { EmptyState, Section, StatusPill, relativeShort } from '../components/UIHelpers';
import { isRevoked, type BridgeTokenInfo, type MintedBridgeToken } from '../store/BridgeTokensStore';

/** A heartbeat within this window (seconds) counts the engine as "connected". */
const FRESH_HEARTBEAT_SECONDS = 120;

export function SetupView() {
  const env = useEnv();
  const state = useAppState();
  const health = state.snapshot.health;

  // Pairing (mint) state
  const [label, setLabel] = useState('');
  const [isMinting, setIsMinting] = useState(false);
  const [minted, setMinted] = useState<MintedBridgeToken | null>(null);
  const [mintError, setMintError] = useState<string | null>(null);
  const [didCopy, setDidCopy] = useState(false);

  // Existing tokens state
  const [tokens, setTokens] = useState<BridgeTokenInfo[]>([]);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [tokensError, setTokensError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const connected = isConnected(health);

  const loadTokens = useCallback(async () => {
    setIsLoadingTokens(true);
    setTokensError(null);
    try {
      setTokens(await env.bridgeTokens.list());
    } catch (e) {
      setTokensError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoadingTokens(false);
    }
  }, [env]);

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  async function mint() {
    setIsMinting(true);
    setMintError(null);
    setDidCopy(false);
    try {
      const result = await env.bridgeTokens.mint({ label: label.trim() || undefined });
      setMinted(result);
    } catch (e) {
      setMintError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsMinting(false);
    }
  }

  async function copyToken(value: string) {
    try {
      await navigator.clipboard?.writeText(value);
    } catch {
      /* clipboard unavailable — the token is selectable in the field */
    }
    setDidCopy(true);
  }

  async function revoke(t: BridgeTokenInfo) {
    if (!window.confirm(`Revoke “${t.label ?? 'this engine'}”? The paired engine will stop draining tasks on its next poll. This can't be undone.`)) {
      return;
    }
    setRevokingId(t.id);
    try {
      await env.bridgeTokens.revoke(t.id);
      await loadTokens();
    } catch (e) {
      setTokensError(e instanceof Error ? e.message : String(e));
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <>
      {/* 1. Engine status */}
      <Section header="Engine status">
        {health ? (
          <>
            <div className="row">
              <div className="health">
                <span
                  className="health__dot"
                  style={{ color: connected ? 'var(--green)' : 'var(--red)', fontSize: 30 }}
                >
                  {connected ? '⚡' : '⭘'}
                </span>
                <div>
                  <div className="row__title" style={{ color: connected ? 'var(--green)' : 'var(--red)', fontSize: 18 }}>
                    {connected ? 'Connected' : 'Offline'}
                  </div>
                  <div className="caption">
                    {health.last_heartbeat
                      ? `Last heartbeat ${relativeShort(health.last_heartbeat)}`
                      : 'No heartbeat reported yet'}
                  </div>
                </div>
              </div>
            </div>
            <div className="row row__head">
              <span className="caption" style={{ flex: 1 }}>
                Daemon
              </span>
              <span>{health.daemon_alive ? 'Alive' : 'Down'}</span>
            </div>
            <div className="row row__head">
              <span className="caption" style={{ flex: 1 }}>
                Watchdog
              </span>
              <span>{health.watchdog_state ? capitalize(health.watchdog_state) : '—'}</span>
            </div>
            {!connected && health.daemon_alive ? (
              <div className="row caption">
                ⚠ The daemon is marked alive but its heartbeat is stale — it may have stalled.
              </div>
            ) : null}
          </>
        ) : (
          <div className="row">
            <EmptyState
              icon="🔌"
              title="No engine paired yet"
              message="Once you pair a local engine below, its live status appears here."
            />
          </div>
        )}
      </Section>

      {/* 2. Pair a new engine */}
      {minted ? (
        <MintedTokenCard
          minted={minted}
          didCopy={didCopy}
          onCopy={() => void copyToken(minted.token)}
          onDone={() => {
            setMinted(null);
            setLabel('');
            setDidCopy(false);
            void loadTokens();
          }}
        />
      ) : (
        <Section header="Pair a new engine">
          <div className="row">
            <input
              className="setup__input"
              value={label}
              placeholder="Label (optional, e.g. “M4 Air”)"
              onChange={(e) => setLabel(e.target.value)}
              aria-label="Token label"
            />
            <div className="btn-row">
              <button className="btn btn--primary" disabled={isMinting} onClick={() => void mint()}>
                {isMinting ? 'Generating…' : '🔑 Generate pairing token'}
              </button>
            </div>
            {mintError ? (
              <div className="caption" style={{ color: 'var(--red)', marginTop: 8 }}>
                ⚠ {mintError}
              </div>
            ) : null}
            <div className="caption" style={{ marginTop: 8 }}>
              Generates a one-time bridge token that lets a local engine drain only your tasks. The full
              plaintext is shown once.
            </div>
          </div>
        </Section>
      )}

      {minted ? <PairingSteps tenantId={state.session?.tenantId ?? null} /> : null}

      {/* 3. Existing tokens */}
      <Section header="Paired engines">
        {isLoadingTokens && tokens.length === 0 ? (
          <div className="spinner" />
        ) : tokensError && tokens.length === 0 ? (
          <div className="row caption">⚠ {tokensError}</div>
        ) : tokens.length === 0 ? (
          <div className="row muted">No bridge tokens yet. Generate one above to pair an engine.</div>
        ) : (
          tokens.map((t) => (
            <TokenRow
              key={t.id}
              token={t}
              revoking={revokingId === t.id}
              onRevoke={() => void revoke(t)}
            />
          ))
        )}
      </Section>
      <div className="row caption" style={{ margin: '0 16px', border: 'none', padding: '8px 6px 0' }}>
        Tokens are stored hashed; only their labels and activity are shown here — never the secret.
      </div>
      <div style={{ height: 24 }} />
    </>
  );
}

function MintedTokenCard({
  minted,
  didCopy,
  onCopy,
  onDone,
}: {
  minted: MintedBridgeToken;
  didCopy: boolean;
  onCopy: () => void;
  onDone: () => void;
}) {
  return (
    <Section header="Save this now — you won't see it again" headerIcon="🛡" warn>
      <div className="row">
        <pre className="setup__token">{minted.token}</pre>
        <div className="btn-row" style={{ marginTop: 12 }}>
          <button className="btn btn--primary" onClick={onCopy}>
            {didCopy ? '✓ Copied' : '📋 Copy token'}
          </button>
          <button className="btn btn--secondary" style={{ color: 'var(--text)', background: 'var(--bg-inset)' }} onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    </Section>
  );
}

function PairingSteps({ tenantId }: { tenantId: string | null }) {
  const steps: Array<[string, string]> = [
    [
      'Set it as the daemon’s token',
      'In the Mac bridge daemon set BRIDGE_TOKEN to this value (its launchd plist substitutes __BRIDGE_TOKEN__ at install time).',
    ],
    [
      'Enable cloud mirroring',
      'In ~/.agent-orchestrator/config.json set cloud_mirror.enabled = true and cloud_mirror.tenant_id to your tenant id (your account id).',
    ],
    [
      'Restart the engine',
      'Reload the daemon (launchctl unload/load) and restart agent-orchestrator. It begins mirroring factory_* rows and draining commands.',
    ],
  ];
  return (
    <Section header="Then, on the machine">
      {steps.map(([title, detail], i) => (
        <div className="row" key={i}>
          <div className="row__head" style={{ alignItems: 'flex-start' }}>
            <span className="setup__step-num">{i + 1}</span>
            <div style={{ flex: 1 }}>
              <div className="row__title" style={{ fontSize: 14 }}>
                {title}
              </div>
              <div className="caption" style={{ marginTop: 2 }}>
                {detail}
              </div>
            </div>
          </div>
        </div>
      ))}
      {tenantId ? (
        <div className="row row__head">
          <span className="caption" style={{ flex: 1 }}>
            Your tenant_id
          </span>
          <span className="mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {tenantId}
          </span>
        </div>
      ) : null}
      <div className="row caption">See docs/OPERATOR_GUIDE.md (Step d–e) for the full pairing runbook.</div>
    </Section>
  );
}

function TokenRow({
  token,
  revoking,
  onRevoke,
}: {
  token: BridgeTokenInfo;
  revoking: boolean;
  onRevoke: () => void;
}) {
  const revoked = isRevoked(token);
  return (
    <div className="row">
      <div className="row__head">
        <span aria-hidden>{revoked ? '🚫' : '🖥'}</span>
        <span className="row__title" style={{ color: revoked ? 'var(--text-secondary)' : undefined }}>
          {token.label ?? 'Unnamed engine'}
        </span>
        {token.executor ? <StatusPill text={token.executor} color="var(--indigo)" /> : null}
        <span className="row__spacer" />
        {revoked ? (
          <StatusPill text="Revoked" color="var(--text-tertiary)" />
        ) : revoking ? (
          <span className="caption">Revoking…</span>
        ) : (
          <button
            className="setup__revoke"
            onClick={onRevoke}
            title="Revoke this bridge token"
          >
            Revoke
          </button>
        )}
      </div>
      <div className="caption" style={{ marginTop: 4 }}>
        Created {relativeShort(token.created_at)} ·{' '}
        {token.last_used_at ? `Last used ${relativeShort(token.last_used_at)}` : 'Never polled'}
      </div>
    </div>
  );
}

// MARK: - Helpers

function isConnected(health: { daemon_alive: boolean; last_heartbeat: string | null } | null): boolean {
  if (!health || !health.daemon_alive || !health.last_heartbeat) return false;
  const then = Date.parse(health.last_heartbeat);
  if (Number.isNaN(then)) return false;
  return (Date.now() - then) / 1000 <= FRESH_HEARTBEAT_SECONDS;
}

function capitalize(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}
